/**
 * Pure display rules for calibration rows, shared by the customer calibration
 * list (DeviceCalibrationScreen) and the admin Calibrations sub-tab. Free of
 * React / MobX / service imports so it can be unit-tested alone.
 *
 * Contract: backend-ai docs/DRIVER_ACCESS_AND_LOAD_SYNC.md §7. A calibration
 * recorded in the driver app arrives with `source: "mobile"`, the person who
 * recorded it, the phone's own coefficient (reference only), possibly no
 * field and no header width, and stays `pending` until the server has the
 * machine's telemetry for its window.
 */
import type { TCalibrationAuthor } from "@services/calibrationService";

export type CalibrationStatusKind =
  | "completed"
  | "computing"
  | "waiting" // driver-app run, not computed yet: the machine's data is still on its way
  | "pending"
  | "failed";

export const isMobileCalibration = (c: { source?: string | null }): boolean =>
  c.source === "mobile";

/**
 * What the coefficient cell says. `failed` wins over a stale coefficient —
 * analytics only use completed calibrations, so a green number next to a
 * failed recompute would be a lie. Otherwise a coefficient on hand is shown
 * (a recompute after an edit briefly reads pending/computing).
 */
export function calibrationStatusKind(c: {
  status?: string | null;
  coefficient?: number | null;
  source?: string | null;
}): CalibrationStatusKind {
  const status = (c.status ?? "").trim().toLowerCase();
  if (status === "failed") return "failed";
  if (c.coefficient != null) return "completed";
  if (status === "computing") return "computing";
  if ((status === "" || status === "pending") && isMobileCalibration(c)) return "waiting";
  return "pending";
}

/** The stored failure reason, or null when there is none worth showing. */
export function calibrationErrorText(c: { error?: string | null }): string | null {
  const e = c.error?.trim();
  return e ? e : null;
}

/**
 * Crop/field cell: the stored name, or an em dash when there is none — a
 * driver-app run on no known field has a null id and no name.
 */
export function calibrationPlaceLabel(c: {
  culture?: { name?: string | null } | null;
}): string {
  return c.culture?.name?.trim() || "—";
}

/** "Recorded by" name: the name, else the email; null when nobody is on record. */
export function calibrationRecorderLabel(c: {
  recordedBy?: TCalibrationAuthor | null;
}): string | null {
  const by = c.recordedBy;
  if (!by) return null;
  return by.name?.trim() || by.email?.trim() || null;
}

/**
 * Header width the calibration was saved with, in metres. Null when none was
 * entered (driver-app runs): the backend lists that as 0, and the machine's
 * width timeline applies instead.
 */
export function calibrationWidthMeters(c: {
  reaper_width?: { value: number | null } | null;
}): number | null {
  const v = c.reaper_width?.value;
  return v != null && Number.isFinite(v) && v > 0 ? v : null;
}

/** The phone's own coefficient (reference only), when it sent one. */
export function phoneCoefficientOf(c: { phoneCoefficient?: number | null }): number | null {
  const v = c.phoneCoefficient;
  return v != null && Number.isFinite(v) ? v : null;
}

/** Same precision as the server coefficient beside it. */
export const formatCoefficient = (v: number): string => v.toFixed(4);

/**
 * Crop the edit dialog starts with: the stored id only when it is one of the
 * listed crops. A driver-app calibration stores none, or a field id the server
 * detected — neither is a crop the picker can show.
 */
export function initialCropId(
  stored: string | null | undefined,
  cropIds: readonly string[]
): string {
  return stored != null && cropIds.includes(stored) ? stored : "";
}

/**
 * The `fieldId` to send when the dialog is saved. A listed crop is sent as
 * is. With none picked, a new calibration is incomplete (`null`; the backend
 * requires one), while an edit leaves it out (`undefined`) so whatever is
 * stored stays — the admin may still pick one.
 */
export function cropIdToSave(
  selected: string,
  cropIds: readonly string[],
  editing: boolean
): string | undefined | null {
  if (selected !== "" && cropIds.includes(selected)) return selected;
  return editing ? undefined : null;
}
